import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import language from '../../variables/language';
import IconAddPerson from '../../components/icons/IconAddPerson';

const RESULT_CAP = 40;

// CEFR progression, beginner (green) -> advanced (purple) - reuses accent
// colors already established elsewhere in this session where possible.
const LEVEL_COLORS = {
   A0: { color: '#3ddf93', bg: 'rgba(61, 223, 147, 0.14)' },
   A1: { color: '#63dac8', bg: 'rgba(99, 218, 200, 0.14)' },
   A2: { color: '#7ec2e8', bg: 'rgba(126, 194, 232, 0.14)' },
   B1: { color: '#e0b04c', bg: 'rgba(224, 176, 76, 0.14)' },
   B2: { color: '#f0954c', bg: 'rgba(240, 149, 76, 0.14)' },
   C1: { color: '#e0876c', bg: 'rgba(224, 135, 108, 0.14)' },
   C2: { color: '#c77dff', bg: 'rgba(199, 125, 255, 0.14)' },
};
const levelStyle = (name) => {
   const cfg = LEVEL_COLORS[name];
   if(!cfg) {
      return { color: 'var(--secondarytext-color)' };
   }
   return { color: cfg.color, borderColor: cfg.color, background: cfg.bg };
}

const formatDate = (val) => {
   if(!val) {
      return '';
   }
   const dt = DateTime.fromISO(val.replace(/\[.*\]/, ''), { setZone: true });
   return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '';
}

// per-plate: how many questions the template has, and how many students
// already have it assigned - fetched lazily per rendered plate (mirrors the
// existing per-tile stats pattern in ConversationTile.jsx/useConversationStats)
// rather than bloating the main list request for plates that may never be seen.
const ConversationPlateMeta = ({ conversationId }) => {
   const [questionsCount, setQuestionsCount] = useState(null);
   const [usersCount, setUsersCount] = useState(null);
   const lang = language[language.lang];

   useEffect(() => {
      let cancelled = false;
      axios.get('/api/answers/?conversation=' + conversationId, { withCredentials: true })
         .then(response => { if(!cancelled) setQuestionsCount(Array.isArray(response.data) ? response.data.length : 0); })
         .catch(() => { if(!cancelled) setQuestionsCount(0); });
      axios.get('/api/messages/stats/' + conversationId, { withCredentials: true })
         .then(response => { if(!cancelled) setUsersCount((response.data && response.data.startedCount) || 0); })
         .catch(() => { if(!cancelled) setUsersCount(0); });
      return () => { cancelled = true; };
   }, [conversationId]);

   return (
      <div className='conv-template-plate-meta'>
         <span className='conv-template-plate-meta-item conv-template-plate-meta-questions'>{questionsCount === null ? '…' : questionsCount} {lang.questionsShort}</span>
         <span className='conv-template-plate-meta-item conv-template-plate-meta-users'>
            <IconAddPerson style={{width: 12, height: 12, margin: 0, fill: '#7ec2e8'}} />
            {usersCount === null ? '…' : usersCount}
         </span>
      </div>
   );
}

// most personal conversations share the same auto-generated name ("Закріплення
// знань"), so name alone can't tell them apart - date + description are what
// actually distinguishes them, both shown on each plate and in the trigger.
export default function ConversationTemplatePicker({ value, onChange, placeholder }) {
   const [conversations, setConversations] = useState([]);
   const [levels, setLevels] = useState([]);
   const [types, setTypes] = useState([]);
   const [loading, setLoading] = useState(false);
   const [query, setQuery] = useState('');
   const [levelFilter, setLevelFilter] = useState('');
   const [typeFilter, setTypeFilter] = useState('');
   const [open, setOpen] = useState(false);
   const wrapRef = useRef(null);
   const lang = language[language.lang];

   // fetched lazily on first open - this list can run into the hundreds of
   // entries, no reason to pay for it on every student-card page load
   useEffect(() => {
      if(!open || conversations.length > 0 || loading) {
         return;
      }
      setLoading(true);
      Promise.all([
         axios.get('/api/conversations?isPublic=0', { withCredentials: true }),
         axios.get('/api/levels', { withCredentials: true }),
         axios.get('/api/conversationtypes', { withCredentials: true }),
      ]).then(([convRes, levelsRes, typesRes]) => {
         const list = Array.isArray(convRes.data) ? convRes.data : [];
         list.sort((a, b) => (b.dAdd || '').localeCompare(a.dAdd || ''));
         setConversations(list);
         setLevels(Array.isArray(levelsRes.data) ? levelsRes.data : []);
         setTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
      }).catch(() => setConversations([]))
        .finally(() => setLoading(false));
   }, [open, conversations.length, loading]);

   useEffect(() => {
      if(!value) {
         setQuery('');
      }
   }, [value]);

   useEffect(() => {
      const handleOutside = (e) => {
         if(wrapRef.current && !wrapRef.current.contains(e.target)) {
            setOpen(false);
         }
      };
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
   }, []);

   const selected = conversations.find(c => String(c.id) === String(value));

   const filtered = conversations.filter(c => {
      if(levelFilter && (!c.level || String(c.level.id) !== levelFilter)) {
         return false;
      }
      if(typeFilter && (!c.typeId || String(c.typeId.id) !== typeFilter)) {
         return false;
      }
      if(!query.trim()) {
         return true;
      }
      const haystack = ((c.name || '') + ' ' + (c.description || '')).toLowerCase();
      return haystack.includes(query.trim().toLowerCase());
   }).slice(0, RESULT_CAP);

   const displayLabel = (c) => c.name + (formatDate(c.dAdd) ? ' · ' + formatDate(c.dAdd) : '');

   const handlePick = (conversation) => {
      onChange(String(conversation.id));
      setQuery(displayLabel(conversation));
      setOpen(false);
   }

   const handleInputChange = (e) => {
      setQuery(e.target.value);
      if(value) {
         onChange('');
      }
   }

   const displayValue = open ? query : (selected ? displayLabel(selected) : query);

   return (
      <div className='conv-template-picker' ref={wrapRef}>
         <input className='submit-field from-input txt-c conv-template-picker-input'
            type='text' placeholder={placeholder}
            value={displayValue}
            onFocus={() => { setOpen(true); setQuery(''); }}
            onChange={handleInputChange} />
         {open ? (
            <div className='conv-template-picker-dropdown'>
               {conversations.length > 0 ? (
                  <div className='conv-template-picker-filters'>
                     <select className='conv-template-filter-select' value={levelFilter}
                        onChange={e => setLevelFilter(e.target.value)}>
                        <option value=''>{lang.level}</option>
                        {levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                     </select>
                     <select className='conv-template-filter-select' value={typeFilter}
                        onChange={e => setTypeFilter(e.target.value)}>
                        <option value=''>{lang.type}</option>
                        {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                     </select>
                  </div>
               ) : ''}
               <div className='conv-template-picker-grid'>
                  {loading ? <div className='conv-template-picker-empty'>{lang['Loading ...']}</div> : ''}
                  {!loading && filtered.length === 0 ? (
                     <div className='conv-template-picker-empty'>{lang['Empty list']}</div>
                  ) : ''}
                  {!loading ? filtered.map(c => (
                     <div key={c.id} className='conv-template-plate' onClick={() => handlePick(c)}>
                        <div className='conv-template-plate-top'>
                           <span className='conv-template-plate-name'>{c.name}</span>
                           {c.level ? <span className='conv-template-plate-level' style={levelStyle(c.level.name)}>{c.level.name}</span> : ''}
                        </div>
                        <div className='conv-template-plate-bottom'>
                           <span className='conv-template-plate-date'>{formatDate(c.dAdd)}</span>
                           {c.description ? <span className='conv-template-plate-desc'>{c.description}</span> : ''}
                        </div>
                        <ConversationPlateMeta conversationId={c.id} />
                     </div>
                  )) : ''}
               </div>
            </div>
         ) : ''}
      </div>
   );
}
